import { RequestType } from "../../../models/requestType";
import { isMappedPurposeUsable } from "./requestTypePurposeResolver";
import { GroundingState } from "../../../models/intake";

/**
 * Resolves the AI-extracted clinic NAME to the clinic ID the form field actually holds,
 * for `seedClinicFromName` (AB#35083).
 *
 * The three outcomes are deliberately distinct, because the caller does something
 * different with each and conflating two of them is what caused the bug:
 *
 *   - `notLoaded` - the institution list isn't available yet. TRANSIENT: the caller must
 *     leave its one-shot seed guard unset and retry on a later update.
 *   - `noMatch`   - the list IS loaded and the AI's clinic name simply isn't in it. FINAL:
 *     the caller burns the seed, because re-running this on every subsequent update would
 *     never produce a different answer.
 *   - `matched`   - seed this id.
 *
 * `seedClinicFromName` used to collapse the first two: it guarded on `if (!di) return`,
 * but `discloseInstitutions` initialises to `[]` (DataEntryDetailsDucks) and **`[]` is
 * truthy**, so an extraction that landed before the clinic list read as "loaded, no
 * match", burned the seed against an empty array, and short-circuited every later retry.
 * The clinic then never reached the form - so the row still rendered the AI's text (it
 * reads requestAnalysis.data directly) while the dropdown, which binds to the form,
 * opened on "- Select -".
 *
 * Never logs a value - clinic/facility names are PII-adjacent (.claude/rules/hipaa-compliance.md).
 */
export type ClinicSeedTarget =
    | { status: "notLoaded" }
    | { status: "noMatch" }
    | { status: "matched"; id: number };

export function resolveClinicSeedTarget(clinicName: string, discloseInstitutions: any): ClinicSeedTarget {
    const list: readonly any[] = Array.isArray(discloseInstitutions)
        ? discloseInstitutions
        : discloseInstitutions
            ? Object.values(discloseInstitutions)
            : [];

    // Empty means not-yet-fetched, not "no such clinic": the reducer's initial value is an
    // empty array, and a facility with zero institutions to disclose from isn't a real state.
    if (list.length === 0) {
        return { status: "notLoaded" };
    }

    const target = clinicName.trim().toLowerCase();
    const match = list.find((c: any) => c?.name?.trim().toLowerCase() === target);
    return match?.id != null ? { status: "matched", id: Number(match.id) } : { status: "noMatch" };
}

export default resolveClinicSeedTarget;

/**
 * What `seedRequestTypePurpose` should write for the OTHER half of AB#35083 (fixed by
 * 97dfcfa). Unlike the clinic seed this has no load-order race: `requestTypeMapped` and
 * `requestPurposeMapped` arrive together on one `requestAnalysis.data`, so there is never a
 * window where one is resolvable and the other isn't.
 *
 * Two properties that fix rests on, extracted here so they are pinned by a runnable test:
 * the pair is decided together (Purpose is never seeded onto a Type the specialist chose
 * later), and an absent purpose leaves Purpose UNSEEDED rather than guessing - the purpose-missing
 * state where the extracted purpose wasn't a valid child of the resolved type and the specialist has
 * to pick one. That renders RED / NeedsEntry, not amber - see the AB#35076 note below.
 *
 * AB#35076 (regression): both ids are now checked against the live catalog, because until this
 * they were seeded AT FACE VALUE and nothing on the path validated them. The ids come from the
 * intake record, which ROIS reads verbatim - ResolvingRequestAnalysisRetriever states "NO
 * read-time catalog fetch and NO local resolver" (Spec 35098) - and the client-side fallback
 * resolver returns the analysis UNTOUCHED whenever a type id is already present
 * (requestTypePurposeResolver.ts). So when the extraction produced a type/purpose pair that is
 * not valid TOGETHER, the purpose id was written into a Purpose dropdown whose options are
 * scoped to the selected type's own children (RequestTypePurposeField), leaving a selection with
 * no matching option. The docblock above asserted this invariant; it did not enforce it, because
 * no catalog was passed in to enforce it against.
 *
 * An invalid purpose degrades to Type-only. That row renders as NeedsEntry - RED and un-confirmable,
 * NOT amber: purposeUncertain was deliberately removed from the amber tier (RequestTypePurposeField
 * "mimics Classic: red and un-confirmable"), and the server flag is false on these payloads anyway.
 * Two earlier docblocks called this amber and were wrong (review threads 53055/53056, Nelson Omuto).
 *
 * An unresolvable TYPE seeds nothing, same as a missing one. Note this currently leaves NO on-screen
 * explanation: the red "not a listed type" hint needs requestTypeMapped to be null (DataEntryDetails
 * typeNotExact), so a phantom id that is set-but-absent-from-the-catalog fires nothing at all
 * (review thread 53134, David Heifetz #1). Seeding nothing is still correct - a phantom Type
 * selection is the identical defect one field over - but the missing hint is tracked separately
 * rather than widened here.
 *
 * Child membership is read from `type.requestPurpose`, never from `purpose.typeID` - that field is
 * deprecated and "not set by the API in all cases" (models/requestType.ts).
 *
 * Never logs a value - extracted type/purpose strings are PHI-adjacent
 * (.claude/rules/hipaa-compliance.md).
 */
export interface RequestTypePurposeSeed {
    readonly requestType: number;
    readonly requestPurpose?: number;
}

export function resolveRequestTypePurposeSeed(
    requestTypeMapped: number | undefined,
    requestPurposeMapped: number | undefined,
    requestTypes: readonly RequestType[] | undefined,
    purposeConfidence: GroundingState | undefined,
): RequestTypePurposeSeed | null {
    if (!requestTypeMapped) {
        return null; // no resolved type - nothing to seed, and a lone purpose has no type to hang off
    }
    // The catalog loads asynchronously and starts as [] (DataEntryDetailsDucks), and [] is TRUTHY -
    // the exact trap that produced the clinic-seed bug documented above. Validating against a
    // not-yet-loaded catalog would reject every valid id, so bail and let a later pass decide.
    // Safe to return null here: seedRequestTypePurpose burns its one-shot seededQeFields latch
    // AFTER this guard, so an undecided pass does not consume the latch and the retry still seeds.
    if (!requestTypes || requestTypes.length === 0) {
        return null;
    }
    const type = requestTypes.find(candidate => candidate.id === requestTypeMapped);
    if (!type) {
        return null;
    }
    // One shared rule (isMappedPurposeUsable): the id must be a child of THIS type AND must not be
    // one the extraction reports as InferredNoSource. Membership alone is not enough - QA's three
    // cases all substituted a VALID child of the right type for the purpose the document stated.
    return isMappedPurposeUsable(requestPurposeMapped, type, purposeConfidence)
        ? { requestType: requestTypeMapped, requestPurpose: requestPurposeMapped }
        : { requestType: requestTypeMapped };
}
